import useHemiPopup from "@/hooks/useHemiPopup";
import { MESSAGE_TAG } from "@/schemas";
import isEqual from "fast-deep-equal";
import { Suspense, lazy, memo } from "react";
import MessageList from "./List";
import MessageProvider from "./Provider";
import { useMessageContext } from "./context";
import type { MessageStoreState } from "./store";

const InputTextAi = lazy(() => import("./input/InputTextAi"));
const InputDiagnosis = lazy(() => import("./drawer/InputDiagnosis"));
const InputPlan = lazy(() => import("./drawer/InputPlan"));
const InputQuestion = lazy(() => import("./drawer/InputQuestion"));
const InputTopic = lazy(() => import("./drawer/InputTopic"));
const InputFiles = lazy(() => import("./drawer/InputFiles"));
const InputMedia = lazy(() => import("./drawer/InputMedia"));
const InputExternal = lazy(() => import("./drawer/InputExternal"));
const InputPublish = lazy(() => import("./drawer/InputPublish"));
const MessageInput = lazy(() => import("./input"));
const ViewOnlyRemark = lazy(() => import("./ViewOnlyRemark"));

interface MessagesProps extends MessageStoreState {
	focusId?: number;
	renderEmpty: React.ReactNode;
}

type InputProps = Pick<
	MessageStoreState,
	"module" | "channelType" | "members" | "search" | "channelName"
>;

const Input = (props: InputProps) => {
	const setShouldStickToBottom = useMessageContext(
		(s) => s.setShouldStickToBottom,
	);

	if (props.search?.tab === MESSAGE_TAG.DIAGNOSIS) {
		return (
			<Suspense>
				<InputDiagnosis members={props.members ?? []} />
			</Suspense>
		);
	}

	if (props.search?.tab === MESSAGE_TAG.PLAN) {
		return (
			<Suspense>
				<InputPlan members={props.members ?? []} />
			</Suspense>
		);
	}

	if (props.search?.tab === MESSAGE_TAG.QUESTION) {
		return (
			<Suspense>
				<InputQuestion members={props.members ?? []} />
			</Suspense>
		);
	}

	if (props.search?.tab === MESSAGE_TAG.TOPIC) {
		return (
			<Suspense>
				<InputTopic members={props.members ?? []} />
			</Suspense>
		);
	}

	if (props.search?.tab === MESSAGE_TAG.FILES) {
		return (
			<Suspense>
				<InputFiles />
			</Suspense>
		);
	}

	if (props.search?.tab === MESSAGE_TAG.EXTERNAL) {
		return (
			<Suspense>
				<InputExternal />
			</Suspense>
		);
	}

	if (props.search?.tab === MESSAGE_TAG.MEDIA) {
		return (
			<Suspense>
				<InputMedia />
			</Suspense>
		);
	}

	if (props.search?.tab === MESSAGE_TAG.PUBLISH) {
		return (
			<Suspense>
				<InputPublish />
			</Suspense>
		);
	}

	if (props.search?.tab === MESSAGE_TAG.AI && props.channelName) {
		return (
			<Suspense>
				<InputTextAi channelName={props.channelName} />
			</Suspense>
		);
	}

	return (
		<Suspense>
			<MessageInput
				className="container bg-white py-2"
				channelType={props.channelType}
				chatModule={props.module}
				members={props.members}
				search={props.search}
				onSuccessSubmit={() => setShouldStickToBottom(true)}
			/>
		</Suspense>
	);
};

const Messages: React.FC<React.PropsWithChildren<MessagesProps>> = ({
	children,
	focusId,
	renderEmpty,
	...props
}) => {
	const isClosed = props.isReadonly ?? false;
	const isViewOnly = props.isViewOnly ?? false;
	const memberIds = props.members?.map((member) => member.id) ?? [];

	useHemiPopup({
		disabled: props.search?.tab !== MESSAGE_TAG.AI,
	});

	return (
		<MessageProvider {...props}>
			<main className="relative flex-1 overflow-hidden">
				<MessageList
					focusId={focusId}
					memberIds={memberIds}
					renderEmpty={renderEmpty}
				/>
			</main>
			{isViewOnly ? (
				<Suspense>
					<ViewOnlyRemark />
				</Suspense>
			) : null}
			{!isViewOnly && !isClosed ? (
				<Input
					channelName={props.channelName}
					channelType={props.channelType}
					module={props.module}
					members={props.members}
					search={props.search}
				/>
			) : null}
		</MessageProvider>
	);
};

export default memo(Messages, isEqual);
